import {Fragment} from "react";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {TableNoDataFoundComponent} from "~/components/ottopilotDataViewer/tableNoDataFoundComponent";
import type {OttopilotView, ViewType} from "~/components/ottopilotDataViewer/view";
import {useTableRows} from "~/components/ottopilotDataViewer/viewDataHooks";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {twJoin} from "tailwind-merge";

export function CardsView<
    T extends {
        id: Uuid;
    },
>({
    controller,
    view,
    className,
}: {
    controller: OttopilotDataViewerController<T>;
    view: OttopilotView<T> & {
        viewType: ViewType.cards;
    };
    className?: string;
}) {
    const rowsQuery = useTableRows(controller);
    const loadedData = rowsQuery.status === "success" ? rowsQuery.data : [];

    return loadedData.length === 0 ? (
        <TableNoDataFoundComponent />
    ) : (
        <div className="w-full h-full grid grid-cols-1 gap-y-ds-space-100 content-start overflow-y-auto">
            <ItemBuilder
                items={loadedData}
                itemBuilder={(item) => (
                    <Fragment key={item.id}>
                        {controller.readPanelComponent !== undefined ? (
                            <div
                                role="button"
                                tabIndex={0}
                                className={twJoin(
                                    "cursor-pointer rounded-[0.75rem] transition-opacity duration-small hover:opacity-90",
                                )}
                                onClick={() => {
                                    controller.setCurrentlyOpenRowEngagementPanel({
                                        rowEngagementType: RowEngagementType.read,
                                        rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                        rowEngagementData: item,
                                    });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        controller.setCurrentlyOpenRowEngagementPanel({
                                            rowEngagementType: RowEngagementType.read,
                                            rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                            rowEngagementData: item,
                                        });
                                    }
                                }}
                            >
                                {view.cardRenderer(item)}
                            </div>
                        ) : (
                            view.cardRenderer(item)
                        )}
                    </Fragment>
                )}
            />
        </div>
    );
}
